import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import ImageSlide from './ImageSlide'
import VideoSlide from './VideoSlide'
import FallbackLogo from '../FallbackLogo'
import './mediaCarousel.css'

const IMAGE_DISPLAY_DURATION = 5000 // 5 seconds

export default function MediaCarousel({ content }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [loadErrors, setLoadErrors] = useState(new Set())
  const timerRef = useRef(null)

  // Reset carousel when content changes (e.g., branch office change)
  useEffect(() => {
    setCurrentIndex(0)
    setLoadErrors(new Set())
  }, [content])

  const validContent = useMemo(
    () => content.filter(item => !loadErrors.has(item.Id)),
    [content, loadErrors]
  )

  const activeIndex =
    validContent.length === 0
      ? 0
      : Math.min(currentIndex, validContent.length - 1)
  const currentItem = validContent[activeIndex]
  const nextIndex =
    validContent.length > 1 ? (activeIndex + 1) % validContent.length : 0

  // Advance to next item in carousel
  const advanceToNext = useCallback(() => {
    setCurrentIndex(prev =>
      validContent.length > 1 ? (prev + 1) % validContent.length : 0
    )
  }, [validContent.length])

  // Handle media load errors
  const handleMediaError = useCallback(
    item => {
      if (!item?.Id) return

      const failedIndex = validContent.findIndex(
        contentItem => contentItem.Id === item.Id
      )
      const nextContentLength = validContent.length - 1

      console.error('Failed to load media:', item.MediaUrl)
      setLoadErrors(prev => new Set([...prev, item.Id]))
      setCurrentIndex(prev => {
        if (nextContentLength <= 0) return 0
        if (failedIndex === -1) return Math.min(prev, nextContentLength - 1)
        if (failedIndex < prev) return prev - 1
        if (failedIndex === prev && prev >= nextContentLength) return 0
        return prev
      })
    },
    [validContent]
  )

  useEffect(() => {
    if (currentIndex !== activeIndex) {
      setCurrentIndex(activeIndex)
    }
  }, [activeIndex, currentIndex])

  // Auto-advance for images
  useEffect(() => {
    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }

    // Only set timer for images (videos use onEnded event)
    if (validContent.length > 1 && currentItem?.MediaType === 'image') {
      timerRef.current = setTimeout(() => {
        advanceToNext()
      }, IMAGE_DISPLAY_DURATION)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [advanceToNext, currentItem, validContent.length])

  // Preload next item for smooth transitions
  useEffect(() => {
    if (validContent.length <= 1) return

    const nextItem = validContent[nextIndex]

    if (!nextItem) return

    if (nextItem.MediaType === 'image') {
      const img = new Image()
      img.src = nextItem.MediaUrl
    }
  }, [nextIndex, validContent])

  // If all content failed to load, show fallback logo
  if (validContent.length === 0) {
    return <FallbackLogo />
  }

  return (
    <div className="media-carousel" data-testid="media-carousel">
      {validContent.map((item, index) => {
        const isActive = index === activeIndex
        const shouldPreload = isActive || index === nextIndex

        if (item.MediaType === 'image') {
          return (
            <ImageSlide
              key={item.Id}
              src={item.MediaUrl}
              alt={item.Title || 'Screensaver image'}
              isActive={isActive}
              onError={() => handleMediaError(item)}
            />
          )
        }

        if (item.MediaType === 'video') {
          const singleItem = validContent.length === 1
          return (
            <VideoSlide
              key={item.Id}
              src={item.MediaUrl}
              isActive={isActive}
              loop={singleItem}
              preload={shouldPreload ? 'auto' : 'none'}
              poster={item.PosterUrl || item.ThumbnailUrl}
              onEnded={singleItem ? undefined : advanceToNext}
              onError={() => handleMediaError(item)}
            />
          )
        }

        return null
      })}
    </div>
  )
}
